import { useCallback, useEffect, useState } from "react";
import { Modal, Button, Spinner, Badge } from "flowbite-react";
import {
  auditarFotosPorLoja,
  limparArquivosSemRegistro,
  limparRegistrosSemArquivo,
} from "../../api/fotos";

function BlocoLista({
  title,
  items,
  emptyText,
  actionLabel,
  onAction,
  actionDisabled = false,
  actionColor = "warning",
}) {
  return (
    <div className="border rounded-lg p-3 bg-gray-50">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h4 className="text-sm font-semibold text-gray-700">{title}</h4>
        {onAction ? (
          <Button
            size="xs"
            color={actionColor}
            onClick={onAction}
            disabled={actionDisabled || items.length === 0}
          >
            {actionLabel}
          </Button>
        ) : null}
      </div>

      {items.length === 0 ? (
        <p className="text-xs text-gray-500">{emptyText}</p>
      ) : (
        <ul className="space-y-1 max-h-40 overflow-auto text-xs text-gray-700">
          {items.map((item, idx) => (
            <li key={idx} className="border-b border-gray-200 pb-1">
              {Object.entries(item).map(([key, value]) => (
                <div key={key}>
                  <span className="font-medium">{key}:</span> {String(value)}
                </div>
              ))}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const FotoAuditoriaModal = ({ isOpen, onClose, lojaId, canEdit = true }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [actionLoading, setActionLoading] = useState("");
  const [feedback, setFeedback] = useState(null);

  const carregarAuditoria = useCallback(async () => {
    if (!lojaId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await auditarFotosPorLoja(lojaId);
      setReport(data);
    } catch (err) {
      setError(err.message || "Nao foi possivel auditar as fotos.");
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [lojaId]);

  useEffect(() => {
    if (isOpen && lojaId) {
      carregarAuditoria();
      setFeedback(null);
    }
  }, [isOpen, lojaId, carregarAuditoria]);

  const handleLimparArquivosSemRegistro = async () => {
    if (!lojaId) return;

    setActionLoading("arquivos");
    setFeedback(null);
    try {
      const resultado = await limparArquivosSemRegistro(lojaId);
      setFeedback({ type: "success", message: resultado.message });
      await carregarAuditoria();
    } catch (err) {
      setFeedback({
        type: "error",
        message: err.message || "Erro ao limpar arquivos sem registro.",
      });
    } finally {
      setActionLoading("");
    }
  };

  const handleLimparRegistrosSemArquivo = async () => {
    if (!lojaId) return;

    setActionLoading("registros");
    setFeedback(null);
    try {
      const resultado = await limparRegistrosSemArquivo(lojaId);
      setFeedback({ type: "success", message: resultado.message });
      await carregarAuditoria();
    } catch (err) {
      setFeedback({
        type: "error",
        message: err.message || "Erro ao limpar registros sem arquivo.",
      });
    } finally {
      setActionLoading("");
    }
  };

  return (
    <Modal show={isOpen} onClose={onClose} size="4xl">
      <Modal.Header>Auditoria de Fotos</Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="flex justify-center py-16">
            <Spinner size="xl" />
          </div>
        ) : null}

        {!loading && error ? (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">
            {error}
          </div>
        ) : null}

        {!loading && !error && feedback ? (
          <div
            className={`text-sm rounded p-3 border ${
              feedback.type === "success"
                ? "text-green-700 bg-green-50 border-green-200"
                : "text-red-600 bg-red-50 border-red-200"
            }`}
          >
            {feedback.message}
          </div>
        ) : null}

        {!loading && !error && report ? (
          <div className="space-y-4">
            {!canEdit ? (
              <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
                O relatório continua disponível, mas as ações de limpeza ficam
                bloqueadas enquanto o plano estiver inativo.
              </div>
            ) : null}
            <div className="flex flex-wrap items-center gap-2">
              <Badge color={report.ok ? "success" : "warning"}>
                {report.ok ? "Sem inconsistencias" : "Inconsistencias encontradas"}
              </Badge>
              <span className="text-xs text-gray-500">
                Gerado em: {new Date(report.generatedAt).toLocaleString("pt-BR")}
              </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              <div className="border rounded p-2">
                <p className="text-gray-500">Arquivos na pasta</p>
                <p className="font-semibold">{report.summary.totalArquivosPasta}</p>
              </div>
              <div className="border rounded p-2">
                <p className="text-gray-500">Registros no banco</p>
                <p className="font-semibold">{report.summary.totalRegistrosBanco}</p>
              </div>
              <div className="border rounded p-2">
                <p className="text-gray-500">Total inconsistencias</p>
                <p className="font-semibold">{report.summary.totalInconsistencias}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <BlocoLista
                title={`Arquivos sem registro (${report.summary.arquivosSemRegistro})`}
                items={report.inconsistencias.arquivosSemRegistro}
                emptyText="Nenhum arquivo orfao na pasta."
                actionLabel={actionLoading === "arquivos" ? "Limpando..." : "Limpar lista"}
                onAction={handleLimparArquivosSemRegistro}
                actionDisabled={!canEdit || actionLoading !== ""}
                actionColor="warning"
              />
              <BlocoLista
                title={`Registros sem arquivo (${report.summary.registrosSemArquivo})`}
                items={report.inconsistencias.registrosSemArquivo}
                emptyText="Nenhum registro com arquivo ausente."
                actionLabel={actionLoading === "registros" ? "Limpando..." : "Limpar lista"}
                onAction={handleLimparRegistrosSemArquivo}
                actionDisabled={!canEdit || actionLoading !== ""}
                actionColor="failure"
              />
              <BlocoLista
                title={`Links invalidos (${report.summary.linksInvalidos})`}
                items={report.inconsistencias.linksInvalidos}
                emptyText="Nenhum link invalido."
              />
            </div>
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button color="light" onClick={carregarAuditoria} disabled={loading || actionLoading !== ""}>
          Rodar novamente
        </Button>
        <Button color="gray" onClick={onClose}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FotoAuditoriaModal;
